import type {
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  SiteMapBlock,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import { ROAD_DRAW_ORDER } from '@lib/dto/site-features.dto'
import type { FeaturePoint } from '@lib/dto/site-features.dto'
import { cn } from '@/lib/utils'
import { labelPoint } from '@/map/labelPoint'
import {
  AREA_WASH_CLASSES,
  CANOPY_RADIUS_METERS,
  canopyClass,
  planAreaColors,
  priorityClass,
  type Lens,
  type TreeStatus,
} from '@/proposal-document/canopy'
import { placedRepairs, repairPriority } from '@/proposal-document/repairs'

/**
 * The site as a flat plan: roads, buildings and water in the band colors,
 * the boundary and areas over them, every tree as a numbered canopy, and
 * every irrigation repair as a numbered tag in the zone it was found in.
 *
 * A picture rather than a map. It is what the cover opens on when there is no
 * photo, and what the printed copy carries, because it draws the same at any
 * size, needs no tiles to arrive before the page is captured, and prints as
 * sharply as the type beside it. The interactive map on screen is the
 * explorer; this is its still.
 */

const WIDTH = 1000

/** Meters per degree of latitude, near enough everywhere a crew works. */
const METERS_PER_DEGREE = 111_320

interface Frame {
  west: number
  north: number
  /** Viewbox units per degree of longitude and of latitude. */
  xScale: number
  yScale: number
  height: number
  metersPerUnit: number
}

function frameFor(block: SiteMapBlock, aspectMax: number): Frame | null {
  const points: { latitude: number; longitude: number }[] = [
    ...(block.boundary?.vertices ?? []),
    ...block.trees.map((tree) => ({
      latitude: tree.latitude,
      longitude: tree.longitude,
    })),
    ...(block.irrigation?.zones.flatMap((zone) => zone.boundary.vertices) ?? []),
    ...(block.irrigation?.repairs ?? []),
  ]
  if (points.length === 0) return null

  let south = Infinity
  let north = -Infinity
  let west = Infinity
  let east = -Infinity
  for (const point of points) {
    south = Math.min(south, point.latitude)
    north = Math.max(north, point.latitude)
    west = Math.min(west, point.longitude)
    east = Math.max(east, point.longitude)
  }

  // Square the projection at this latitude so circles stay round.
  const middle = ((south + north) / 2) * (Math.PI / 180)
  const lonFactor = Math.cos(middle)
  // A margin so canopies at the edge are not cut, and a floor so a single
  // tree still has a frame around it.
  const spanY = Math.max(north - south, 0.0004)
  const spanX = Math.max((east - west) * lonFactor, 0.0004)
  const padY = spanY * 0.12
  const padX = spanX * 0.12
  let width = spanX + padX * 2
  let height = spanY + padY * 2
  // Too tall a plan pushes everything else off the page, so a tall site is
  // drawn smaller with room either side. Never cropped: a tree or repair cut
  // off the plan is one the reader is told is on it.
  if (height > width * aspectMax) width = height / aspectMax
  height = Math.max(height, width * 0.42)

  const scale = WIDTH / width
  const centerY = (south + north) / 2
  const centerX = (west + east) / 2
  return {
    west: centerX - width / 2 / lonFactor,
    north: centerY + height / 2,
    xScale: scale * lonFactor,
    yScale: scale,
    height: height * scale,
    metersPerUnit: METERS_PER_DEGREE / scale,
  }
}

function project(frame: Frame, latitude: number, longitude: number): [number, number] {
  return [
    (longitude - frame.west) * frame.xScale,
    (frame.north - latitude) * frame.yScale,
  ]
}

/** Area names on the plan, in viewbox units. */
const AREA_NAME_SIZE = 13

/** About how wide a name is set, per character, against its size. */
const CHARACTER_WIDTH = 0.58

/**
 * The area names to write on the plan under the Areas view, each at the
 * point of its ring farthest from the edges. Biggest ring first, and one is
 * left off when it would not fit in its ring or would run into a name
 * already written, as on the live map.
 */
function areaNamesOf(
  block: SiteMapBlock,
  frame: Frame,
): { key: string; x: number; y: number; text: string }[] {
  const placed: { left: number; right: number; top: number; bottom: number }[] = []
  const rings = (block.areas ?? []).map((area) => {
    const points = area.boundary.vertices.map((v) => project(frame, v.latitude, v.longitude))
    const xs = points.map(([x]) => x)
    const ys = points.map(([, y]) => y)
    const width = Math.max(...xs) - Math.min(...xs)
    const height = Math.max(...ys) - Math.min(...ys)
    return { area, width, height }
  })
  rings.sort((a, b) => b.width * b.height - a.width * a.height)
  return rings.flatMap(({ area, width, height }) => {
    const textWidth = area.name.length * AREA_NAME_SIZE * CHARACTER_WIDTH
    if (width < textWidth * 0.75 || height < AREA_NAME_SIZE * 1.2) return []
    const at = labelPoint(area.boundary.vertices)
    const [x, y] = project(frame, at.latitude, at.longitude)
    const box = {
      left: x - textWidth / 2 - 4,
      right: x + textWidth / 2 + 4,
      top: y - AREA_NAME_SIZE / 2 - 4,
      bottom: y + AREA_NAME_SIZE / 2 + 4,
    }
    if (
      placed.some(
        (other) =>
          box.left < other.right &&
          other.left < box.right &&
          box.top < other.bottom &&
          other.top < box.bottom,
      )
    ) {
      return []
    }
    placed.push(box)
    return [{ key: area.id, x, y, text: area.name }]
  })
}

function pathOf(frame: Frame, points: FeaturePoint[], close: boolean): string {
  const parts = points.map(([latitude, longitude], index) => {
    const [x, y] = project(frame, latitude, longitude)
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
  })
  return close ? `${parts.join('')}Z` : parts.join('')
}

/** A repair's tag: tall enough for its number, and wide enough for "R12". */
const REPAIR_TAG_HEIGHT = 18
const REPAIR_SPREAD = 26

function repairTagWidth(label: string): number {
  return 8 + label.length * 7
}

export interface SitePlanSvgProps {
  block: SiteMapBlock
  rows: TreeScheduleRow[]
  lens?: Lens
  /** How a tree shows: in the plan, or left out. */
  statusOf?: (label: string) => TreeStatus
  /** Trees to emphasize; the rest fade. */
  highlight?: ReadonlySet<string>
  selected?: string | null
  /** Tallest the plan may be, as a share of its width. */
  aspectMax?: number
  /** The site's irrigation schedule, which numbers and colors its repairs. */
  irrigation?: IrrigationScheduleBlock | undefined
  /** How a repair shows: in the plan, or left out. */
  repairStatusOf?: (repair: IrrigationRepairRow) => TreeStatus
  className?: string
}

export function SitePlanSvg({
  block,
  rows,
  lens = 'priority',
  statusOf,
  highlight,
  selected = null,
  aspectMax = 0.75,
  irrigation,
  repairStatusOf,
  className,
}: SitePlanSvgProps) {
  const frame = frameFor(block, aspectMax)
  if (frame === null) return null

  const rowByLabel = new Map(rows.map((row) => [row.label, row]))
  const areaColors = planAreaColors(block.areas ?? [])
  const areaColorOf = (name: string | null | undefined) =>
    name == null ? null : (areaColors.get(name) ?? null)
  const features = block.features
  const height = Math.round(frame.height)
  const repairs = placedRepairs(block, irrigation)

  return (
    <svg
      viewBox={`0 0 ${String(WIDTH)} ${String(height)}`}
      className={cn('proposal-plan', className)}
      role="img"
      aria-label={planLabel(block.siteName, block.trees.length, repairs.length)}
    >
      <rect width={WIDTH} height={height} className="plan-ground" />
      {block.boundary === null ? null : (
        <path
          d={pathOf(
            frame,
            block.boundary.vertices.map((v): FeaturePoint => [v.latitude, v.longitude]),
            true,
          )}
          className="plan-site"
        />
      )}
      {features === null ? null : (
        <g>
          {features.water.map((ring, index) => (
            <path key={`w${String(index)}`} d={pathOf(frame, ring, true)} className="plan-water" />
          ))}
          {ROAD_DRAW_ORDER.flatMap((kind) =>
            features.roads
              .filter((road) => road.kind === kind)
              .map((road, index) => (
                <g key={`${kind}${String(index)}`}>
                  <path d={pathOf(frame, road.points, false)} className={`plan-road-casing plan-road--${kind}`} />
                  <path d={pathOf(frame, road.points, false)} className={`plan-road plan-road--${kind}`} />
                </g>
              )),
          )}
          {features.buildings.map((ring, index) => (
            <path key={`b${String(index)}`} d={pathOf(frame, ring, true)} className="plan-building" />
          ))}
        </g>
      )}
      {(block.areas ?? []).map((area) => {
        const d = pathOf(
          frame,
          area.boundary.vertices.map((v): FeaturePoint => [v.latitude, v.longitude]),
          true,
        )
        const color = areaColorOf(area.name)
        return (
          <g key={area.id}>
            {lens === 'areas' && color !== null ? (
              <path d={d} className={AREA_WASH_CLASSES[color]} />
            ) : null}
            <path d={d} className="plan-area" />
          </g>
        )
      })}
      {(block.irrigation?.zones ?? []).map((zone) => (
        <path
          key={zone.name}
          d={pathOf(
            frame,
            zone.boundary.vertices.map((v): FeaturePoint => [v.latitude, v.longitude]),
            true,
          )}
          className="plan-zone"
        />
      ))}
      {block.boundary === null ? null : (
        <path
          d={pathOf(
            frame,
            block.boundary.vertices.map((v): FeaturePoint => [v.latitude, v.longitude]),
            true,
          )}
          className="plan-boundary"
        />
      )}
      {block.trees.map((tree) => {
        const row = rowByLabel.get(tree.label)
        const [x, y] = project(frame, tree.latitude, tree.longitude)
        // Never smaller than a mark you can read a number in.
        const r = Math.max(9, CANOPY_RADIUS_METERS / frame.metersPerUnit)
        const status = statusOf?.(tree.label) ?? 'in'
        const fill =
          row === undefined || status !== 'in'
            ? ''
            : canopyClass(lens, row, areaColorOf(row.areaName), 'fill')
        const faded = highlight !== undefined && !highlight.has(tree.label)
        return (
          <g
            key={tree.label}
            className={cn(
              'plan-tree',
              `plan-tree--${status}`,
              faded && 'plan-tree--faded',
              selected === tree.label && 'plan-tree--selected',
            )}
          >
            {selected === tree.label ? (
              <circle cx={x} cy={y} r={r + 5} className="plan-tree-ring" />
            ) : null}
            <circle cx={x} cy={y} r={r} className={cn('plan-canopy', fill)} />
            <text x={x} y={y} className="plan-label" fontSize={Math.max(10, Math.min(16, r * 0.8))}>
              {tree.label.replace(/^#/, '')}
            </text>
          </g>
        )
      })}
      {repairs.map((entry) => {
        const [x, y] = project(frame, entry.latitude, entry.longitude)
        const width = repairTagWidth(entry.label)
        const status = repairStatusOf?.(entry.repair) ?? 'in'
        const cx = x + entry.dx * REPAIR_SPREAD
        const cy = y + entry.dy * REPAIR_SPREAD
        return (
          <g key={entry.key} className={cn('plan-repair', `plan-repair--${status}`)}>
            <rect
              x={cx - width / 2}
              y={cy - REPAIR_TAG_HEIGHT / 2}
              width={width}
              height={REPAIR_TAG_HEIGHT}
              rx={4}
              className={cn(
                'plan-repair-tag',
                status === 'in' &&
                  (lens === 'priority'
                    ? priorityClass(repairPriority(entry.repair))
                    : 'canopy--water'),
              )}
            />
            <text x={cx} y={cy} className="plan-label" fontSize={11}>
              {entry.label}
            </text>
          </g>
        )
      })}
      {/* Over the trees, so a name is read rather than guessed from a swatch. */}
      {lens === 'areas'
        ? areaNamesOf(block, frame).map((name) => (
            <text
              key={`name-${name.key}`}
              x={name.x}
              y={name.y}
              className="plan-area-name"
              fontSize={AREA_NAME_SIZE}
            >
              {name.text}
            </text>
          ))
        : null}
      {features === null ? null : (
        <text x={WIDTH - 8} y={height - 8} className="plan-credit">
          © OpenStreetMap contributors
        </text>
      )}
    </svg>
  )
}

/** What a screen reader hears for the plan. */
function planLabel(siteName: string, trees: number, repairs: number): string {
  const counts = [
    trees === 0 ? null : `${String(trees)} numbered ${trees === 1 ? 'tree' : 'trees'}`,
    repairs === 0
      ? null
      : `${String(repairs)} numbered irrigation ${repairs === 1 ? 'repair' : 'repairs'}`,
  ].filter((part): part is string => part !== null)
  return counts.length === 0
    ? `Site plan of ${siteName}`
    : `Site plan of ${siteName} with ${counts.join(' and ')}`
}
